import React, { useEffect, useRef, useState, useContext } from "react";
import { FiAlertTriangle, FiEye, FiEyeOff } from "react-icons/fi";
import { AuthContext } from "../../../../../context/AuthContext";
import { changeUserPassword } from "../../../../../services/NexiDigitalAPI";
import { useToast } from "hooks/useToast";
import "./style.scss";

type ResetPasswordModalProps = {
  open: boolean;
  onClose?: () => void;
  idUser: number | string;
  userName: string;
  onSuccess?: () => void;
};

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  open,
  onClose,
  idUser,
  userName,
  onSuccess,
}) => {
  const { loginData } = useContext(AuthContext as unknown as React.Context<any>);
  const { success, error } = useToast();

  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [showNew, setShowNew] = useState<boolean>(false);
  const [showConfirm, setShowConfirm] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [touchedNew, setTouchedNew] = useState<boolean>(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setNewPassword("");
      setConfirmPassword("");
      setShowNew(false);
      setShowConfirm(false);
      setConfirmed(false);
      setSubmitting(false);
      setTouchedNew(false);
      setTimeout(() => firstInputRef.current?.focus(), 0);
    }
  }, [open]);

  if (!open) return null;

  const hasMinLen = newPassword.length >= 8;
  const hasLetter = /[A-Za-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const meetsPolicy = hasMinLen && hasLetter && hasNumber;
  const showRequirements = touchedNew && !meetsPolicy;

  const isValid =
    meetsPolicy &&
    confirmPassword.length > 0 &&
    newPassword === confirmPassword &&
    confirmed;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      const response = await changeUserPassword(idUser, newPassword);
      const status = response?.status;
      const ok = status === 200;

      if (!ok) {
        const msg =
          response?.message ||
          (status === 403
            ? "Você não tem autorização para alterar a senha deste usuário. Use o envio de e-mail."
            : "Não foi possível redefinir a senha. Tente novamente.");
        error(msg);
        setSubmitting(false);
        return;
      }

      success("Senha redefinida com sucesso!");
      onSuccess?.();
      onClose?.();
    } catch {
      error("Não foi possível redefinir a senha. Tente novamente.");
      setSubmitting(false);
    }
  };

  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).classList.contains("ResetPasswordModal__backdrop")) onClose?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") onClose?.();
    if (e.key === "Enter" && isValid) handleSubmit();
  };

  return (
    <div
      className="ResetPasswordModal__backdrop"
      onMouseDown={onBackdropClick}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div
        className="ResetPasswordModal__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="reset-password-title"
      >
        <div className="ResetPasswordModal__header">
          <div className="ResetPasswordModal__iconWrap">
            <FiAlertTriangle />
          </div>
          <h2 id="reset-password-title" className="ResetPasswordModal__title">
            Redefinir senha de
            <br />“{userName}”?
          </h2>
        </div>

        <div className="ResetPasswordModal__content">
          <label className="ResetPasswordModal__label">Nova senha</label>
          <div className="ResetPasswordModal__inputGroup">
            <input
              ref={firstInputRef}
              type={showNew ? "text" : "password"}
              placeholder="Digite a nova senha"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onBlur={() => setTouchedNew(true)}
              className={showRequirements ? "is-invalid" : undefined}
              aria-invalid={showRequirements}
            />
            <button
              type="button"
              className="ResetPasswordModal__eyeBtn"
              onClick={() => setShowNew((p) => !p)}
              aria-label={showNew ? "Ocultar senha" : "Mostrar senha"}
            >
              {showNew ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          {showRequirements && (
            <ul className="ResetPasswordModal__requirements">
              <li>Pelo menos 8 caracteres</li>
              <li>Pelo menos uma letra</li>
              <li>Pelo menos um número</li>
            </ul>
          )}

          <label className="ResetPasswordModal__label">Confirmar nova senha</label>
          <div className="ResetPasswordModal__inputGroup">
            <input
              type={showConfirm ? "text" : "password"}
              placeholder="Digite a nova senha"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={!meetsPolicy}
            />
            <button
              type="button"
              className="ResetPasswordModal__eyeBtn"
              onClick={() => setShowConfirm((p) => !p)}
              aria-label={showConfirm ? "Ocultar senha" : "Mostrar senha"}
            >
              {showConfirm ? <FiEyeOff /> : <FiEye />}
            </button>
          </div>

          {newPassword && confirmPassword && newPassword !== confirmPassword && (
            <div className="ResetPasswordModal__errorMsg">As senhas não coincidem.</div>
          )}

          <label className="ResetPasswordModal__confirm">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span>
              Confirmo a redefinição de senha.
              <b className="ResetPasswordModal__danger"> Esta ação é irreversível.</b>
            </span>
          </label>
        </div>

        <div className="ResetPasswordModal__footer">
          <button
            type="button"
            className="ResetPasswordModal__btnGhost"
            onClick={() => onClose?.()}
            disabled={submitting}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="ResetPasswordModal__btnPrimary"
            style={{
              backgroundColor: isValid ? "#5B36F0" : "#C8BBFA",
              pointerEvents: isValid ? "auto" : "none",
              color: "#fff",
            }}
            onClick={handleSubmit}
          >
            {submitting ? "Salvando..." : "Redefinir senha"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
